import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { GROWTHBOOK } from './growthbook';
import { randomString } from './lib/random-string';
import { PICFLOW_MAINTENANCE } from './maintenance';
import { PICFLOW_CONFIG } from './picflow-config';
import { NetworkInterceptorHelperService } from './services/network-interceptor-helper.service';
import { NetworkService } from './services/network.service';
import { StateService } from './services/state.service';

export const IGNORE_AUTH_ERRORS = new HttpContextToken<boolean>(() => false);
export const IGNORE_PAYMENT_REQUIRED_ERRORS = new HttpContextToken<boolean>(() => false);
export const IGNORE_MAINTENANCE = new HttpContextToken<boolean>(() => false);
export const IGNORE_ALL_ERRORS = new HttpContextToken<boolean>(() => false);
export const WITH_AUTH = new HttpContextToken<boolean>(() => false);
export const FORCE_GUEST = new HttpContextToken<boolean>(() => false);
export const TENANT_CONTEXT = new HttpContextToken<string>(() => undefined);
export const WITH_CHALLENGE = new HttpContextToken<boolean>(() => false);
export const ALLOW_AUTHZ_CACHE = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class NetworkInterceptorService {
  public request$ = new BehaviorSubject<HttpRequest<unknown>>(undefined);
  public response$ = new BehaviorSubject<{
    res: HttpResponse<unknown>;
    req: HttpRequest<unknown>;
    duration: number;
    randomId: string;
  }>(undefined);
  public error$ = new BehaviorSubject<{ error: HttpErrorResponse; req: HttpRequest<unknown> }>(undefined);
}

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {
  private errorWrapper = inject<{ error: Error }>('ERROR_WRAPPER' as never, { optional: true });
  private platformId = inject<string>(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);
  private growthBook = inject(GROWTHBOOK);
  private maintenance = inject(PICFLOW_MAINTENANCE);
  private state = inject(StateService);
  private helperService = inject(NetworkInterceptorHelperService);
  private networkInterceptor = inject(NetworkInterceptorService);
  private networkService = inject(NetworkService);

  // intercept function
  public intercept(req: HttpRequest<unknown>, next: HttpHandler, errorCount = 0): Observable<HttpEvent<unknown>> {
    // returning an observable to complete the request cycle
    this.networkInterceptor.request$.next(req);
    const start = Date.now();

    const randomId = randomString(5);

    return of(this.addRandomParam(req, randomId))
      .pipe(
        switchMap(_req => this.addAuthHeaders(_req)),
        map(_req => this.addTenant(_req)),
        map(_req => this.addAuthzCache(_req)),
        switchMap(_req => next.handle(_req))
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
          errorCount++;
          const retryCount = this.getRetryCount(req, err);
          const delay = this.getRetryDelay(err);

          if (errorCount > retryCount) return this.handleError(req, err, errorCount, Date.now() - start);
          return delay.pipe(switchMap(() => this.intercept(req, next, errorCount)));
        }),
        tap((res: HttpResponse<unknown>) =>
          this.networkInterceptor.response$.next({ res, req, duration: Date.now() - start, randomId })
        )
      );
  }

  private getRetryDelay(error: HttpErrorResponse): Observable<unknown> {
    if (isPlatformServer(this.platformId)) return of(undefined);
    if (error.status === 404 || error.status === 422) return of(undefined);
    return forkJoin([this.networkService.online$, of(1000)]);
  }

  private getRetryCount(req: HttpRequest<unknown>, error: HttpErrorResponse) {
    if (isPlatformServer(this.platformId)) return 0;
    if (req.url.includes('/bulk') && error.status === 0) return 0; // https://app.asana.com/0/1201026654052703/1208145473529777
    if (this.config.captureOneLocalHost && req.url.includes(this.config.captureOneLocalHost)) return 0;
    if (
      error.status === 404 ||
      error.status === 422 ||
      error.status === 400 ||
      error.status === 503 ||
      error.status === 402
    ) {
      return 0;
    }
    if (error.error?.code === 'password_required') return 0;
    if (req.url.includes('/internal-api/')) return 0;
    if (error.status === 0) return 3;
    return 1;
  }

  private handleError(req: HttpRequest<unknown>, error: HttpErrorResponse, errorCount: number, duration: number) {
    if (this.config.env === 'local' && isPlatformBrowser(this.platformId)) {
      console.debug(req.url, req.method, { errorCount, duration, status: error.status });
    } else {
      console.debug(req.url, req.method, error.error, { errorCount, duration });
    }

    if (req.context.get(IGNORE_ALL_ERRORS)) return throwError(() => error);
    if (req.context.get(IGNORE_AUTH_ERRORS) && [401, 403].includes(error.status)) return throwError(() => error);
    if (req.context.get(IGNORE_PAYMENT_REQUIRED_ERRORS) && error.status === 402) return throwError(() => error);
    this.networkInterceptor.error$.next({ error: error, req });

    const code = error?.error?.code;
    if (code) {
      if (error.status === 503 && code === 'maintenance_exception') {
        if (!req.context.get(IGNORE_MAINTENANCE)) {
          this.handleMaintenanceMode(error);
        }
      }
      return throwError(() => error); // if there is a code, then  the backend isn't completely down so it would have been able to return a maintenance error if there was one
    }

    if (this.maintenance) {
      return this.maintenance.getRealTimeMaintenanceMode().pipe(
        switchMap(maintenance => {
          if (maintenance && !req.context.get(IGNORE_MAINTENANCE)) {
            this.handleMaintenanceMode(error);
          }
          return throwError(() => error);
        })
      );
    }

    return throwError(() => error);
  }

  private handleMaintenanceMode(error: HttpErrorResponse) {
    if (isPlatformBrowser(this.platformId)) {
      const returnUrl = location.href.replace(location.origin, '');
      if (!window.onbeforeunload && !location.href.includes('/maintenance')) {
        location.href = `${location.origin}/maintenance?returnUrl=${encodeURIComponent(returnUrl)}`;
      }
    }
    if (this.errorWrapper) this.errorWrapper.error = error;
  }

  private addTenant(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint) || req.url.includes(this.config.internalApiUrlBase);
    if (!apiRequest) return req;
    const headers = this.helperService.getTenantHeaders(req.context.get(TENANT_CONTEXT));
    if (!headers) return req;
    return req.clone({ setHeaders: headers });
  }

  private addAuthzCache(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint) || req.url.includes(this.config.internalApiUrlBase);
    if (!apiRequest) return req;
    if (!req.context.get(ALLOW_AUTHZ_CACHE)) return req;
    return req.clone({ url: req.url + (req.url.includes('?') ? '&' : '?') + 'authz_consistency=minimize_latency' });
  }

  private addRandomParam(req: HttpRequest<unknown>, randomId: string) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    if (!apiRequest) return req;
    return req.clone({ url: req.url + (req.url.includes('?') ? '&' : '?') + 'r=' + randomId });
  }

  private addAuthHeaders(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    const requiresAuth = apiRequest || req.context.get(WITH_AUTH);

    const guestAuthRequest = req.url.includes('/auth/guest');
    if (!requiresAuth) return of(req);
    if (guestAuthRequest) return of(req);

    const headers: { [name: string]: string | string[] } = {};

    if (isPlatformServer(this.platformId)) {
      headers['x-ssr-origin'] = this.config.origin;
    }

    const guest = !!this.state.guestAuthToken;
    const user = guest && req.context.get(FORCE_GUEST) ? undefined : this.state.tokenData;
    const observable = user
      ? this.helperService.getUserHeaders()
      : guest
        ? this.helperService.getGuestHeaders()
        : of({});

    return observable.pipe(map(authHeaders => req.clone({ setHeaders: { ...authHeaders, ...headers } })));
  }
}
